// ──────────────────────────────────────────────────────
// Onboarding — frontend-only shared constants.
//
// Single source of truth for rotating hints + phone
// pre-validation used by both /m/q (mobile) and the
// desktop ChatDrawer, so the two surfaces can't drift.
// ──────────────────────────────────────────────────────

/** Rotating hints rendered tightly under the first AI onboarding bubble. */
export const ONBOARDING_HINTS: readonly string[] = [
    'أرشدك لأفضل منتج حسب ميزانيتك',
    'أسوي لك روتين كامل لبشرتك',
    'أقارن لك بين منتجين بسرعة',
    'أوضح لك الفرق بين منتج وآخر',
    'أساعدك تختار قبل ما تشتري',
    'أرشح لك مناسب للبشرة الدهنية',
    'أرشح لك مناسب للبشرة الجافة',
    'أرشح لك مناسب للبشرة الحساسة',
    'أرشح لك مناسب للشعر الجاف',
    'أرشح لك مناسب للشعر المجعد',
    'أقترح لك بدائل بنفس الفائدة',
    'أشرح لك طريقة استخدام المنتج',
    'أبسّط لك الخيارات المعقدة',
    'أساعدك تقرر إذا المنتج يناسبك',
    'أجاوبك عن أي منتج تسأل عنه',
    'أوصيك بمنتج يناسب هدفك',
    'أوضح لك فوائد المكونات بسرعة',
    'أختصر لك قائمة المنتجات',
];

/**
 * Classify a first-send Saudi-mobile phone input locally so the user gets
 * precise short/long/prefix copy before involving the backend. Returns
 * 'not-phone' for shapes that probably aren't phone attempts (OTP codes,
 * names, etc.) so we pass through without interception.
 */
export function classifyPhoneInput(raw: string): 'ok' | 'short' | 'long' | 'prefix' | 'not-phone' {
    const digits = raw
        .replace(/[\u0660-\u0669]/g, d => String(d.charCodeAt(0) - 0x0660))
        .replace(/[\u06F0-\u06F9]/g, d => String(d.charCodeAt(0) - 0x06F0))
        .replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15) return 'not-phone';
    if (/^05\d{8}$/.test(digits)) return 'ok';
    if (/^5\d{8}$/.test(digits)) return 'ok';
    if (/^9665\d{8}$/.test(digits)) return 'ok';
    if (/^009665\d{8}$/.test(digits)) return 'ok';
    if (digits.length < 9) return 'short';
    if (digits.length > 12) return 'long';
    return 'prefix';
}

export const PHONE_ERROR_COPY: Record<'short' | 'long' | 'prefix', string> = {
    short: 'يبان إن الرقم ناقص شوي 👀\nرقم الجوال في السعودية يكون 10 أرقام، جرّب تكتبه كامل 👍',
    long: 'الرقم أطول من المطلوب 😅\nتأكد إنه 10 أرقام بس (مثال: 05XXXXXXXX)',
    prefix: 'تأكد من رقم الجوال 🙏\nغالبًا يبدأ بـ 05',
};

// ──────────────────────────────────────────────────────
// OTP interception helpers — shared by both the mobile
// /m/q page and the desktop ChatDrawer so the two surfaces
// route successful OTP through the same auth endpoint and
// receive the same httpOnly `qio_session` cookie.
// ──────────────────────────────────────────────────────

/**
 * Minimal test for "looks like an OTP code". Accepts 4-8 digit strings
 * (whitespace/dashes tolerated on paste, Arabic-Indic digits normalized
 * in the caller via normalizeDigits). Rejects anything that isn't
 * essentially a numeric code so a stray name containing a number
 * never triggers the auth-endpoint path.
 */
export function looksLikeOtp(raw: string): boolean {
    // Inline digit normalization — avoids an import cycle with lib/normalize
    // and keeps the helper self-contained. Matches the same Arabic-Indic
    // (٠-٩) + Persian (۰-۹) range used by normalizeDigits().
    const normalized = raw.replace(/[\u0660-\u0669\u06F0-\u06F9]/g, (ch) => {
        const code = ch.charCodeAt(0);
        return String(code <= 0x0669 ? code - 0x0660 : code - 0x06F0);
    });
    const digits = normalized.replace(/[^0-9]/g, '');
    if (digits.length < 4 || digits.length > 8) return false;
    const stripped = normalized.replace(/[\s-]/g, '');
    return /^\d{4,8}$/.test(stripped);
}

export interface VerifyOtpSessionResult {
    ok: boolean;
    status: number;
    customerId?: string;
    isReturning?: boolean;
}

/**
 * Per-tab marker that this browser session has completed mobile OTP and
 * should therefore have a valid `qio_session` httpOnly cookie. The cookie
 * itself isn't readable from JS (httpOnly), so callers that depend on
 * mobile-authenticated state use this flag as their pre-flight gate
 * — most importantly the feedback hydration in useMessageActions, which
 * would otherwise emit a console-visible 401 on every drawer mount when
 * `qcare_customer_id` is present in localStorage from a prior session
 * but the cookie has not been issued yet (e.g. before today's OTP).
 *
 * sessionStorage so that closing the tab requires a fresh OTP — keeps
 * the gate honest about "current session" rather than persisting beyond
 * the cookie's likely scope.
 */
export const MOBILE_OTP_VERIFIED_FLAG = 'qcare_mobile_otp_verified';

/** Mark the current tab as having completed mobile OTP successfully. */
export function markMobileOtpVerified(): void {
    if (typeof window === 'undefined') return;
    try { window.sessionStorage.setItem(MOBILE_OTP_VERIFIED_FLAG, '1'); } catch { /* storage disabled */ }
}

/** Drop the marker after a server-rejected request (e.g. 401), so the next render doesn't re-fire the same losing call. */
export function clearMobileOtpVerified(): void {
    if (typeof window === 'undefined') return;
    try { window.sessionStorage.removeItem(MOBILE_OTP_VERIFIED_FLAG); } catch { /* storage disabled */ }
}

/** Read the marker. Returns false on SSR. */
export function isMobileOtpVerified(): boolean {
    if (typeof window === 'undefined') return false;
    try { return window.sessionStorage.getItem(MOBILE_OTP_VERIFIED_FLAG) === '1'; } catch { return false; }
}

/**
 * POST the OTP code to /api/mobile/auth/verify-otp. On success the proxy
 * forwards the httpOnly `qio_session` Set-Cookie so subsequent same-origin
 * requests are automatically authenticated.
 *
 * `credentials: 'include'` is set even though this is same-origin — it
 * makes the cookie-receiving intent explicit and keeps the call safe if
 * the proxy path is ever moved to a subdomain.
 *
 * Returns a structured result; callers decide whether to fall back to the
 * inline chat OTP flow on non-ok. Never throws — caller doesn't need to
 * try/catch.
 */
export async function verifyOtpSession(
    phone: string,
    otp: string,
    sessionId?: string,
): Promise<VerifyOtpSessionResult> {
    try {
        const res = await fetch('/api/mobile/auth/verify-otp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            // `sessionId` is the caller's current mobile session identity (the
            // same value used as `customerId` on /api/mobile/chat). Passing it
            // lets the backend link this session to the resolved canonical
            // customer inside the verify-otp step — preventing duplicate
            // customer rows and split conversation history.
            body: JSON.stringify({ phone, otp, ...(sessionId ? { sessionId } : {}) }),
        });
        if (!res.ok) return { ok: false, status: res.status };
        const data = await res.json().catch(() => ({}));
        // Cookie issuance is the whole point of this endpoint, so a 200
        // means a `qio_session` Set-Cookie has been forwarded — flag the
        // tab so downstream hydration calls (e.g. /mobile/chat/feedback)
        // know they're allowed to fire.
        markMobileOtpVerified();
        return {
            ok: true,
            status: res.status,
            customerId: typeof data.customerId === 'string' ? data.customerId : undefined,
            isReturning: typeof data.isReturning === 'boolean' ? data.isReturning : undefined,
        };
    } catch {
        return { ok: false, status: 0 };
    }
}

// ──────────────────────────────────────────────────────
// requestOtpSession — POST /api/mobile/auth/send-otp
//
// Companion to `verifyOtpSession` for the dedicated mobile-login
// screen (QCARE-AI-DEV-MOBILE-LOGIN-OTP-BACKEND-WIRING-OPTION-A).
// Backend always returns HTTP 200 on user-recoverable failures
// (cooldown, throttled, provider unavailable) and embeds a
// structured `code` field — the UI branches on `result.code`, not
// HTTP status. Real network/server errors collapse to
// `code: 'NETWORK'`. Never throws.
// ──────────────────────────────────────────────────────

/** Result codes the dedicated send-otp endpoint can return. Mirrors
 *  the backend `RequestOtpResult` discriminator. `'NETWORK'` is a
 *  client-only sentinel for fetch failures / non-2xx HTTP. */
export type RequestOtpFailureCode =
    | 'INVALID_PHONE'
    | 'LOCKED'
    | 'COOLDOWN'
    | 'RATE_LIMIT'
    | 'THROTTLED'
    | 'OTP_DISABLED'
    | 'PROVIDER_UNAVAILABLE'
    | 'PROVIDER_FAILED'
    | 'NETWORK';

export type RequestOtpSessionResult =
    | { ok: true; expiresInSeconds: number; resendAfterSeconds: number }
    | { ok: false; code: RequestOtpFailureCode; message: string; resendAfterSeconds?: number };

const FALLBACK_NETWORK_MESSAGE = 'تعذر إرسال رمز التحقق. جربي مرة ثانية.';

export async function requestOtpSession(
    phone: string,
    sessionId?: string,
): Promise<RequestOtpSessionResult> {
    try {
        const res = await fetch('/api/mobile/auth/send-otp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ phone, ...(sessionId ? { sessionId } : {}) }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            return {
                ok: false,
                code: 'NETWORK',
                message: typeof data?.message === 'string' && data.message.length > 0
                    ? data.message
                    : FALLBACK_NETWORK_MESSAGE,
            };
        }
        if (data && data.ok === true) {
            return {
                ok: true,
                expiresInSeconds: typeof data.expiresInSeconds === 'number' ? data.expiresInSeconds : 300,
                resendAfterSeconds: typeof data.resendAfterSeconds === 'number' ? data.resendAfterSeconds : 60,
            };
        }
        return {
            ok: false,
            code: typeof data?.code === 'string' ? (data.code as RequestOtpFailureCode) : 'PROVIDER_FAILED',
            message: typeof data?.message === 'string' && data.message.length > 0
                ? data.message
                : FALLBACK_NETWORK_MESSAGE,
            resendAfterSeconds: typeof data?.resendAfterSeconds === 'number' ? data.resendAfterSeconds : undefined,
        };
    } catch {
        return { ok: false, code: 'NETWORK', message: FALLBACK_NETWORK_MESSAGE };
    }
}

// ──────────────────────────────────────────────────────
// getAuthMe — GET /api/mobile/auth/me
//
// Cold-start probe used by the mobile chat page to detect a
// deleted / inactive customer whose qio_session cookie was just
// invalidated by the MobileSessionMiddleware. Always returns a
// well-formed { authenticated, ... } shape; never throws into UI;
// network errors collapse to authenticated:false (same effect as
// a missing cookie — the frontend shows login).
// ──────────────────────────────────────────────────────
export interface AuthMeResult {
    authenticated: boolean;
    customerId?: string;
    profileComplete?: boolean;
    // Customer's saved profile name (full, as typed) — trimmed by the
    // backend, or null when the row has no usable name. Drives the
    // mobile drawer footer so returning customers see their own name.
    displayName?: string | null;
    // QCARE-AI-DEV-LANDING-COPY-EXPERIENCE-MODE-PERSONALIZATION
    // (2026-05-03): customer's selected experience mode. Used to
    // gender the mobile landing-screen copy + composer placeholder
    // for returning customers on cold load.
    experienceMode?: 'feminine' | 'masculine' | 'neutral' | null;
    // QCARE-ARABIC-NAME-REQUIRED (2026-05-15): backend-side verdict
    // on `mobile_customers.name`. False when missing / placeholder
    // / English / mixed / too short / too long / invalid chars.
    // The /m/q page routes to the profile (name) step when this
    // is explicitly `false` on a cold-load authenticated session.
    arabicNameValid?: boolean;
    // QCARE-CREDIT-COPY-30 (2026-05-16): per-customer daily message
    // quota + remaining sends in the current 24h window. Replaces a
    // hardcoded `6 / 10` placeholder in the mobile profile screen.
    // Older backends omit these — undefined → preserve existing
    // behavior in callers.
    dailyCredits?: number;
    remainingCredits?: number;
}

export async function getAuthMe(): Promise<AuthMeResult> {
    try {
        // QCARE-AI-DEV-AUTH-ME-NO-STORE-AND-FALLBACK-FIX (2026-05-03):
        // `cache: 'no-store'` plus an explicit Cache-Control request
        // header force iOS Safari (and any UA applying heuristic
        // freshness to a body with no Cache-Control) to revalidate
        // every probe. Belt-and-suspenders with the proxy + backend
        // response headers — any one of the three would suffice on a
        // strict UA, all three are required for Safari's mix.
        const res = await fetch('/api/mobile/auth/me', {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-store' },
        });
        if (!res.ok) return { authenticated: false };
        const data = await res.json().catch(() => null);
        if (!data || typeof data !== 'object') return { authenticated: false };
        if (data.authenticated !== true) return { authenticated: false };
        return {
            authenticated: true,
            customerId: typeof data.customerId === 'string' ? data.customerId : undefined,
            profileComplete: typeof data.profileComplete === 'boolean' ? data.profileComplete : undefined,
            displayName: typeof data.displayName === 'string'
                ? data.displayName
                : data.displayName === null
                    ? null
                    : undefined,
            experienceMode:
                data.experienceMode === 'feminine' ||
                data.experienceMode === 'masculine' ||
                data.experienceMode === 'neutral'
                    ? data.experienceMode
                    : data.experienceMode === null
                        ? null
                        : undefined,
            arabicNameValid: typeof data.arabicNameValid === 'boolean'
                ? data.arabicNameValid
                : undefined,
            dailyCredits: typeof data.dailyCredits === 'number' && Number.isFinite(data.dailyCredits)
                ? data.dailyCredits
                : undefined,
            remainingCredits: typeof data.remainingCredits === 'number' && Number.isFinite(data.remainingCredits)
                ? data.remainingCredits
                : undefined,
        };
    } catch {
        return { authenticated: false };
    }
}

// ──────────────────────────────────────────────────────
// clearInvalidAuthState — drop ONLY auth flags, preserve transcripts.
//
// QCARE-AI-DEV-DELETED-CUSTOMER-AUTH-GATE-FIX-SAFE-PRESERVE-TRANSCRIPTS
// (2026-05-03): when getAuthMe() reports authenticated:false but
// localStorage.qcare_customer_id is still a real (non-anon) id, we
// must reset the auth gate WITHOUT destroying the user's local
// conversation history, image thumbnails, or restored product
// cards. Hazem confirmed those losses regressed UX badly in the
// past — they're protected by name here.
//
// REMOVES (auth/UI flags only):
//   • qcare_login_demo_done                     — legacy demo gate
//   • qcare_mobile_otp_verified  (sessionStorage) — feedback POST gate
//
// PRESERVES (per the spec):
//   • qcare_chat_sessions:<oldCustomerId>
//   • qcare_active_chat_session:<oldCustomerId>
//   • qcare_chat_transcript:<oldCustomerId>:<sessionId>
//   • image thumbnails inside transcript JSON
//   • qcare_mobile_ui_version
//
// Caller is responsible for separately calling setCustomerId(...)
// with a fresh anon_<uuid> so the auth gate's `isReal` check fires
// — `qcare_customer_id` itself is left to the caller's reset path.
// ──────────────────────────────────────────────────────
export function clearInvalidAuthState(): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem('qcare_login_demo_done');
    } catch { /* storage disabled */ }
    try {
        window.sessionStorage.removeItem(MOBILE_OTP_VERIFIED_FLAG);
    } catch { /* storage disabled */ }
}
